import React from 'react';
import {get} from 'lodash';
import {gql} from '@apollo/client';

import client from '../../../src/graphql/client';

import CategoryPage from '../../../src/containers/Community/Category';

import refactorBreadcrumbsData from '../../../src/data-restructure/navigation/breadcrumbs/breadcrumbs';
import refactorHb from '../../../src//data-restructure/multimedia/hero-banner-small-v2-2023';
import respImgQuery from '../../../src/graphql/queries/resp-img';
import refactorRespImg from '../../../src/data-restructure/ui/resp-img';
import seoQuery from '../../../src/graphql/queries/seo';

const Category = (props) => {
  return (
  	<CategoryPage
      {...props}
		/>
  )
}

export async function getStaticPaths() {
  
  const GET_DYNAMIC_ARTICLES_CATEGORIES_QUERY = gql`
    query GET_ARTICLES_CATEGORIES_PATHS {
			articleCategories {
				nodes {
          slug
				}
			}
    }
  `

  const result = await client.query({
    query: GET_DYNAMIC_ARTICLES_CATEGORIES_QUERY
  });

  let paths = [];
  
  const articles = get(result, 'data.articleCategories.nodes', []);

  articles.forEach(item => {
    const slug = get(item, 'slug', null);

    paths.push({
      params: {
        slug: slug
      }
    });
  });

  return { paths, fallback: 'blocking' }
}

export async function getStaticProps({ params }) {
  const countryCode = process.env.DEV_COUNTRY_CODE ? process.env.DEV_COUNTRY_CODE.toLowerCase() : process.env.COUNTRY_CODE.toLowerCase();
  const domain = process.env.DEV_ASSIGNED_DOMAIN ? process.env.DEV_ASSIGNED_DOMAIN : process.env.ASSIGNED_DOMAIN.toLowerCase()

  // Merged query combining all data needs in a single request
  const GET_ALL_CATEGORY_DATA = gql`
    query GET_ALL_CATEGORY_DATA($slug: String!, $communityUri: String!) {
      # Get community page data for hero banner and SEO
      pageBy(uri: $communityUri) {
        title
        seo {
          ${seoQuery}
        }
        uri
        acf_content_builder {
          contentBuilder {
            ... on Page_AcfContentBuilder_ContentBuilder_FcHeroBannerV22023 {
              fieldGroupName
              hb2Banners {
                hb2Title
                hb2Subtext
                hb2Image {
                  ${respImgQuery}
                }
                hb2VideoDesktop {
                  guid
                }
                hb2VideoMobile {
                  guid
                }
                hb2Link {
                  title
                  url
                  target
                }
              }
            }
          }
        }
      }
      
      # Get all article categories for navigation
      allCategories: articleCategories {
        nodes {
          name
          slug
          articles(first: 1) {
            edges {
              node {
                id
              }
            }
          }
        }
      }
      
      # Get specific category by slug with its articles
      specificCategory: articleCategories(where: {slug: $slug}) {
        nodes {
          slug
          id
          name
          articles(first: 1000) {
            nodes {
              id
              uri
              title
              date
              articleCategories {
                nodes {
                  name
                  slug
                }
              }
              acf_article {
                artDatePosted
                artTeaserCopy
                artThumbnailImage {
                  ${respImgQuery}
                }
              }
              acf_domains {
                assignedDomains
              }
            }
          }
        }
      }
    }
  `

  // Single query with all required data
  const allData = await client.query({
    query: GET_ALL_CATEGORY_DATA,
    variables: {
      slug: params.slug,
      communityUri: `community?${countryCode}`
    }
  });

  if (allData) {
    const context = get(allData, 'data.pageBy', null);

    const hbData = get(context, 'acf_content_builder.contentBuilder', null);
    const heroBanner = hbData && hbData[0] ? refactorHb(hbData[0]) : null;

    const categoryNodes = get(allData, 'data.allCategories.nodes', null);

    const categories = categoryNodes ? categoryNodes.filter(item => {
      return get(item, 'articles.edges', []).length > 0 
    }) : null;

    const name = get(allData, 'data.specificCategory.nodes[0].name', null);
    const slug = get(allData, 'data.specificCategory.nodes[0].slug', null);
    const articles = get(allData, 'data.specificCategory.nodes[0].articles.nodes', null);

    const articleConversion = articles ? articles.filter(item => {
      const articleAssignedDomains = get(item, 'acf_domains.assignedDomains', []);
      return articleAssignedDomains.indexOf(domain) !== -1;
    }).map(item => {
      const title = item.title;
      const date = item.date;
      const teaser = item.acf_article.artTeaserCopy;
      const uri = item.uri ? item.uri.replace('-ccau', '').replace('-ccnz', '') : null;
      const category = item.articleCategories.nodes[0].name;
      const img = item.acf_article.artThumbnailImage ? refactorRespImg(item.acf_article.artThumbnailImage, [{
        size: 'default',
        key: 'image_size_466x314'
      }]) : null;
  
      return {
        id: item.id,
        title,
        date,
        teaser,
        uri,
        category,
        img
      }
    }) : [];

    const assignedDomains = get(context, 'acf_domains.assignedDomains', []); 

    // breadcrumbs
    const categoryUrl = slug ? '/community/category/' + slug : false;
    const breadcrumbs = categoryUrl ? await refactorBreadcrumbsData(categoryUrl, assignedDomains) : null;

    const data = {
      meta: {
        title: context.title,
        seo: context.seo
      },
      breadcrumbs,
      heroBanner,
      categories,
      name,
      slug,
      articleConversion
    }

    return {
      revalidate: (process.env.NEXT_STATIC_EXPORT && process.env.NEXT_STATIC_EXPORT.toString() === 'true') ? false : 1,
      props: {
        ...data,
      }
    }
  } else { 
    return { 
      revalidate: (process.env.NEXT_STATIC_EXPORT && process.env.NEXT_STATIC_EXPORT.toString() === 'true') ? false : 1,
      props: {} 
    }
  }
}

export default Category
